import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDistanceToNow } from 'date-fns';
import DuelNotificationActions from './DuelNotificationActions';
import SquadNotificationActions from './SquadNotificationActions';

const typeConfig = {
  goal_achievement: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', icon: 'Trophy' },
  activity_milestone: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent', icon: 'Activity' },
  meal_reminder: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', icon: 'Utensils' },
  sleep_insight: { bg: 'bg-purple-100 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700', text: 'text-purple-600 dark:text-purple-400', icon: 'Moon' },
  duel_invitation: { bg: 'bg-orange-100 dark:bg-orange-900/20', border: 'border-orange-300 dark:border-orange-700', text: 'text-orange-600 dark:text-orange-400', icon: 'Swords' },
  duel_accepted: { bg: 'bg-orange-50 dark:bg-orange-900/10', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-500', icon: 'CheckCircle' },
  duel_completed: { bg: 'bg-orange-50 dark:bg-orange-900/10', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-400', icon: 'Flag' },
  friend_request: { bg: 'bg-primary/10', border: 'border-primary/30', text: 'text-primary', icon: 'UserPlus' },
  friend_accepted: { bg: 'bg-primary/10', border: 'border-primary/30', text: 'text-primary', icon: 'UserCheck' },
  squad_invitation: { bg: 'bg-blue-100 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-700', text: 'text-blue-600 dark:text-blue-400', icon: 'Users' },
  squad_member_joined: { bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-500', icon: 'UserPlus' },
  squad_join_request: { bg: 'bg-blue-100 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-700', text: 'text-blue-600', icon: 'UserSearch' },
  challenge_invitation: { bg: 'bg-purple-100 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700', text: 'text-purple-600 dark:text-purple-400', icon: 'Zap' },
  badge_earned: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', border: 'border-yellow-300 dark:border-yellow-700', text: 'text-yellow-600 dark:text-yellow-400', icon: 'Medal' },
  friend_milestone: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', icon: 'Star' },
};

const categoryColorFallback = {
  green: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', icon: 'Trophy' },
  orange: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', icon: 'Swords' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700', text: 'text-purple-600 dark:text-purple-400', icon: 'Moon' },
  blue: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent', icon: 'Users' },
  yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', border: 'border-yellow-300 dark:border-yellow-700', text: 'text-yellow-600 dark:text-yellow-400', icon: 'Medal' },
};

const NotificationCard = ({ notification, isSelected, onSelect, onMarkAsRead, onDelete, index }) => {
  const navigate = useNavigate();

  const config =
    typeConfig?.[notification?.type] ||
    categoryColorFallback?.[notification?.category_color] ||
    categoryColorFallback?.blue;

  const handleAction = () => {
    if (notification?.action_path) {
      if (!notification?.is_read) {
        onMarkAsRead(notification?.id);
      }
      navigate(notification?.action_path);
    }
  };

  const handleCardClick = () => {
    if (!notification?.is_read) {
      onMarkAsRead(notification?.id);
    }
  };

  const timeAgo = notification?.created_at
    ? formatDistanceToNow(new Date(notification?.created_at), { addSuffix: true })
    : '';

  const isDuelInvitation = notification?.type === 'duel_invitation';
  const isSquadInvitation = notification?.type === 'squad_invitation';
  const needsCustomActions = (isDuelInvitation || isSquadInvitation) && !notification?.is_read;

  // Extract metadata extras for badge/milestone notifications
  const metadata = notification?.metadata || {};
  const badgeIcon = metadata?.badge_icon;
  const badgeName = metadata?.badge_name;
  const milestonePts = metadata?.points_earned;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.05 }}
      className={`relative group bg-card rounded-xl border transition-all duration-300 ${
        notification?.is_read ? 'border-border opacity-75' : `${config?.border} border-l-4`
      } ${
        isSelected ? 'ring-2 ring-primary ring-offset-2' : ''
      } ${
        notification?.priority && !notification?.is_read ? 'shadow-md' : ''
      } hover:shadow-lg hover:scale-[1.01]`}
    >
      <div className="p-4">
        <div className="flex items-start space-x-4">
          {/* Checkbox */}
          <div className="flex-shrink-0 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(notification?.id)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer"
              onClick={(e) => e?.stopPropagation()}
            />
          </div>

          {/* Icon */}
          <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 ${config?.bg} rounded-xl`}>
            {badgeIcon ? (
              <span className="text-2xl">{badgeIcon}</span>
            ) : (
              <Icon name={config?.icon} size={24} className={config?.text} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={handleCardClick}>
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <h3 className={`text-base font-semibold ${
                  notification?.is_read ? 'text-muted-foreground' : 'text-foreground'
                }`}>
                  {notification?.title}
                </h3>
                {notification?.priority && !notification?.is_read && (
                  <Icon name="Star" size={14} className="text-warning" />
                )}
                {!notification?.is_read && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    New
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                {timeAgo}
              </span>
            </div>

            <p className={`text-sm mb-2 ${
              notification?.is_read ? 'text-muted-foreground' : 'text-foreground/80'
            }`}>
              {notification?.message}
            </p>

            {/* Metadata extras */}
            {(badgeName || milestonePts > 0) && (
              <div className="flex items-center space-x-3 mb-2">
                {badgeName && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config?.bg} ${config?.text}`}>
                    {badgeName}
                  </span>
                )}
                {milestonePts > 0 && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-success/10 text-success">
                    +{milestonePts} pts
                  </span>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {needsCustomActions && isDuelInvitation && (
                  <DuelNotificationActions
                    notification={notification}
                    onActionComplete={() => onMarkAsRead(notification?.id)}
                  />
                )}
                {needsCustomActions && isSquadInvitation && (
                  <SquadNotificationActions
                    notification={notification}
                    onActionComplete={() => onMarkAsRead(notification?.id)}
                  />
                )}
                {!needsCustomActions && notification?.action_label && notification?.action_path && (
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleAction();
                    }}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className={`${config?.text} border-current hover:bg-current/10`}
                  >
                    {notification?.action_label}
                  </Button>
                )}
              </div>

              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!notification?.is_read && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onMarkAsRead(notification?.id);
                    }}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Mark as read"
                  >
                    <Icon name="Check" size={16} className="text-muted-foreground" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onDelete(notification?.id);
                  }}
                  className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Icon name="Trash2" size={16} className="text-error" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Unread Indicator Dot */}
      {!notification?.is_read && (
        <div className="absolute top-4 right-4">
          <div className="w-2 h-2 bg-primary rounded-full"></div>
        </div>
      )}
    </motion.div>
  );
};

export default NotificationCard;